/**
 * AP-10 Nachtrag 1, 13.1 — **Eigenes E-Mail-Postfach im Profil.**
 *
 * Verbinden schickt den Browser zu Microsoft; zurück kommt er über den
 * Rückkehr-Endpunkt mit `?mail=connected` oder `?mail=error&code=…`. Das
 * Trennen hängt an keinem Recht — wer sein Recht verloren hat, muss sein Token
 * trotzdem entfernen können.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { fehlertext } from '@/lib/fehlertext'
import { mailApi, type MailConnectionState } from '@/lib/mail/mail.api'
import { bestaetige } from '@/lib/dialoge'

const RUECKKEHR_CODES = ['state_invalid', 'consent_denied', 'admin_consent_required', 'domain_not_allowed', 'not_allowed', 'token_exchange_failed']

export function MailConnectionCard() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const [stand, setStand] = useState<MailConnectionState | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  const laden = () =>
    mailApi
      .getConnection()
      .then(({ data }) => setStand(data.data))
      .catch((err) => setFehler(fehlertext(err, t)))

  useEffect(() => {
    void laden()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Rückkehr von Microsoft: Meldung zeigen, Parameter aus der Adresse nehmen.
  useEffect(() => {
    const ergebnis = params.get('mail')
    if (!ergebnis) return
    const code = params.get('code') ?? ''
    if (ergebnis === 'connected') setMeldung(t('mailConnection.result.connected'))
    else setFehler(t(`mailConnection.result.errors.${RUECKKEHR_CODES.includes(code) ? code : 'token_exchange_failed'}`))
    const rest = new URLSearchParams(params)
    rest.delete('mail')
    rest.delete('code')
    setParams(rest, { replace: true })
  }, [params, setParams, t])

  // Ohne Konfiguration oder bei „aus", ohne Verbindung: gar nicht erst anbieten.
  if (!stand || (!stand.connection && (!stand.available || stand.policy === 'OFF'))) return null

  const verbinden = async () => {
    setFehler(null)
    setLaeuft(true)
    try {
      const { data } = await mailApi.startMicrosoftConnection()
      window.location.assign(data.data.authorizeUrl)
    } catch (err) {
      setFehler(fehlertext(err, t))
      setLaeuft(false)
    }
  }

  const trennen = async () => {
    if (!(await bestaetige(t('mailConnection.disconnectConfirm')))) return
    setFehler(null)
    setLaeuft(true)
    try {
      await mailApi.disconnect()
      setMeldung(t('mailConnection.disconnected'))
      await laden()
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setLaeuft(false)
    }
  }

  const c = stand.connection
  const datum = (iso: string) => new Date(iso).toLocaleString(navigator.language)

  return (
    <div className="rounded-lg border bg-card p-6 shadow-sm">
      <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-foreground">
        <Mail className="h-4 w-4" />
        {t('mailConnection.title')}
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">{t('mailConnection.intro')}</p>

      {stand.policy === 'REQUIRED' && !c && <p className="mb-3 rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('mailConnection.required')}</p>}

      {c ? (
        <div className="space-y-1 text-sm">
          <p className="text-foreground">
            {t('mailConnection.connectedAs', { address: c.address })}
            <span
              className={`ml-2 rounded px-1.5 py-0.5 text-xs ${c.status === 'OK' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-100' : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'}`}
            >
              {t(`mailConnection.status.${c.status}`)}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {t('mailConnection.connectedAt', { date: datum(c.connectedAt) })}
            {c.lastUsedAt ? ` · ${t('mailConnection.lastUsedAt', { date: datum(c.lastUsedAt) })}` : ''}
          </p>
          {c.status === 'EXPIRED' && <p className="text-xs text-muted-foreground">{t('mailConnection.expiredHint')}</p>}
        </div>
      ) : (
        !stand.mayConnect && <p className="text-sm text-muted-foreground">{t('mailConnection.noPermission')}</p>
      )}

      {meldung && <p className="mt-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{meldung}</p>}
      {fehler && <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}

      <div className="mt-6 flex justify-end gap-2">
        {c && (
          <button type="button" disabled={laeuft} onClick={() => void trennen()} className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
            {t('mailConnection.disconnect')}
          </button>
        )}
        {stand.mayConnect && stand.available && stand.policy !== 'OFF' && (!c || c.status === 'EXPIRED') && (
          <button
            type="button"
            disabled={laeuft}
            onClick={() => void verbinden()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {laeuft ? t('common.loading') : t(c ? 'mailConnection.reconnect' : 'mailConnection.connect')}
          </button>
        )}
      </div>
    </div>
  )
}
